// layouts/autenticacion/usuarios/nuevo-usuario.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// MUI
import {
  Card,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Autocomplete,
  Divider,
} from "@mui/material";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Shell
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// API
import { createUser } from "layouts/autenticacion/usuarios/services/users";
import { listGrupos } from "layouts/catalogos/services/grupos";

// Permisos UI
import PermissionUser from "../components/PermissionUser";

/* ---------------- helpers ---------------- */

const MODULE_MAP_UI_TO_API = {
  usuarios: "user",
  reconocimientos: "award",
  reportes: "reportes",
  dashboard: "dashboard",
  sellos: "sellos",
  generos: "generos",
  grupos: "grupos",
  nacionalidades: "nacionalidades",
  certificaciones: "certificaciones",
  parametros: "parametros",
};

const DEFAULT_CRUD = () => ({
  read: false,
  create: false,
  update: false,
  delete: false,
});

const normalizeGroups = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.page)) return res.page;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  return [];
};

function generateTempPassword(len = 16) {
  const alphabet =
    "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*()-_=+";

  if (window?.crypto?.getRandomValues) {
    const arr = new Uint32Array(len);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, (x) => alphabet[x % alphabet.length]).join("");
  }

  let out = "";
  for (let i = 0; i < len; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }

  return out;
}

function permissionKeysToApiObject(keys = []) {
  const out = {};

  for (const k of keys) {
    if (typeof k !== "string" || !k.includes(".")) continue;

    const [uiModule, action] = k.split(".");
    const apiModule = MODULE_MAP_UI_TO_API[uiModule] || uiModule;

    if (!out[apiModule]) out[apiModule] = DEFAULT_CRUD();

    if (
      action === "read" ||
      action === "create" ||
      action === "update" ||
      action === "delete"
    ) {
      out[apiModule][action] = true;
      if (action !== "read") out[apiModule].read = true;
    }
  }

  Object.keys(out).forEach((m) => {
    const p = out[m];

    out[m] = {
      read: !!p.read,
      create: !!p.create,
      update: !!p.update,
      delete: !!p.delete,
    };
  });

  return out;
}

function stripHtml(s) {
  return String(s || "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function extractApiErrors(err) {
  const out = [];
  const res = err?.response;
  const d = res?.data;

  if (typeof d === "string") out.push(stripHtml(d));
  if (d?.message) out.push(d.message);

  if (Array.isArray(d?.errors)) {
    d.errors.forEach((e) => out.push(e?.message || e?.msg || JSON.stringify(e)));
  }

  if (Array.isArray(d) && !d.errors) {
    d.forEach((e) =>
      out.push(typeof e === "string" ? e : JSON.stringify(e))
    );
  }

  if (res?.status) out.push(`HTTP ${res.status}`);

  if (!out.length) out.push(err?.message || "Error de servidor");

  return [...new Set(out)].filter(Boolean);
}

function normalizeGroupIds(values = []) {
  if (!Array.isArray(values)) return [];

  return values
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));
}

const safeString = (x) => {
  try {
    if (x == null) return "";
    if (typeof x === "string") return x;
    if (x?.message && typeof x.message === "string") return x.message;

    if (x?.instancePath || x?.schemaPath || x?.keyword) {
      return `${x.instancePath || ""} ${x.message || ""}`.trim() || JSON.stringify(x);
    }

    return JSON.stringify(x);
  } catch {
    return String(x);
  }
};

function buildUserPayload(form) {
  const email = form.email?.trim();

  const username =
    (form.username?.trim() || (email ? email.split("@")[0] : "")).toLowerCase() ||
    undefined;

  const payload = {
    email,
    username,
    name: form.name?.trim() || undefined,
    first_surname: form.first_surname?.trim() || undefined,
    second_surname: form.second_surname?.trim() || undefined,
    groups: form.is_admin
      ? []
      : normalizeGroupIds(form.groups),
    disabled: !!form.disabled,
    is_admin: !!form.is_admin,
    recovering: true,
  };

  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined || payload[k] === "") delete payload[k];
  });

  return payload;
}

/* ---------------- component ---------------- */

export default function NuevoUsuario() {
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const [form, setForm] = useState({
    email: "",
    username: "",
    name: "",
    first_surname: "",
    second_surname: "",
    groups: [],
    password: "",
    password2: "",
    disabled: false,
    is_admin: false,
  });

  const [permKeys, setPermKeys] = useState([]);

  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  // cargar grupos
  useEffect(() => {
    let cancel = false;

    const fetchGroups = async () => {
      setLoadingGroups(true);

      try {
        const res = await listGrupos({
          __limit: 1000,
          __offset: 0,
          __sort: { name: 1 },
        });

        if (cancel) return;

        const list = normalizeGroups(res);
        setGroups(list);
      } catch (e) {
        if (!cancel) {
          setErrors([
            `No se pudieron cargar los grupos: ${extractApiErrors(e).join(", ")}`,
          ]);
        }
      } finally {
        if (!cancel) setLoadingGroups(false);
      }
    };

    fetchGroups();

    return () => {
      cancel = true;
    };
  }, []);

  const onChange = (e) =>
    setForm((p) => ({
      ...p,
      [e.target.name]: e.target.value,
    }));

  const isEmailValid = (val) =>
    !!val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSave = async () => {
    setErrors([]);

    if (!form.email) return setErrors(["El email es obligatorio"]);
    if (!isEmailValid(form.email)) return setErrors(["El email no es válido"]);
    if (!form.is_admin && (!Array.isArray(form.groups) || form.groups.length === 0)) {
      return setErrors(["Selecciona al menos un grupo"]);
    }

    const base = buildUserPayload(form);

    const tempPassword = generateTempPassword(16);
    const payload = { ...base, password: tempPassword };

    const permissionsObj = permissionKeysToApiObject(permKeys);

    if (!form.is_admin && Object.keys(permissionsObj).length) {
      payload.permissions = permissionsObj;
    }

    try {
      setSaving(true);

      const created = await createUser(payload);

      if (created?.recovery_key) {
        alert(
          `Usuario creado por invitación.\nClave de recuperación:\n${created.recovery_key}\n\nGuárdala y compártela con el usuario.`
        );
      }

      navigate("/autenticacion/usuarios", { replace: true });
    } catch (e) {
      console.error("[NuevoUsuario] createUser ERROR:", e);
      setErrors(extractApiErrors(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftBox py={3}>
        <Grid container spacing={3}>

          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3 }}>
              <SoftBox
                mb={3}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <SoftTypography variant="h6">
                  Información del usuario
                </SoftTypography>

                <SoftButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate(-1)}
                >
                  Volver
                </SoftButton>
              </SoftBox>

              <Alert severity="info" sx={{ mb: 2 }}>
                Al crear el usuario se enviará automáticamente un correo
                para completar el registro y establecer su contraseña.
              </Alert>

              {errors.length > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.map((m, i) => (
                    <div key={i}>{safeString(m)}</div>
                  ))}
                </Alert>
              )}

              <Grid container spacing={2}>

                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    autoComplete="email"
                    required
                    error={!!form.email && !isEmailValid(form.email)}
                    helperText={
                      !!form.email && !isEmailValid(form.email)
                        ? "Email inválido"
                        : " "
                    }
                  />
                </Grid>

                {/* Username */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Usuario (username)"
                    name="username"
                    value={form.username}
                    onChange={onChange}
                    autoComplete="username"
                    helperText="Si lo dejas vacío, se usará la parte antes de @ del email."
                  />
                </Grid>

                {/* Nombre */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                  />
                </Grid>

                {/* Primer apellido */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Primer apellido"
                    name="first_surname"
                    value={form.first_surname}
                    onChange={onChange}
                  />
                </Grid>

                {/* Segundo apellido */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Segundo apellido"
                    name="second_surname"
                    value={form.second_surname}
                    onChange={onChange}
                  />
                </Grid>

                {/* Aquí dejas el Autocomplete multiple de grupos */}
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    disableCloseOnSelect
                    options={groups}
                    value={groups.filter((g) =>
                      form.groups
                        .map(String)
                        .includes(String(g.id ?? g._id))
                    )}
                    onChange={(_event, selectedGroups) => {
                      setForm((p) => ({
                        ...p,
                        groups: selectedGroups.map((g) =>
                          String(g.id ?? g._id)
                        ),
                      }));
                    }}
                    getOptionLabel={(g) =>
                      g?.name ??
                      g?.nombre ??
                      `Grupo ${g?.id ?? g?._id ?? ""}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      String(option.id ?? option._id) ===
                      String(value.id ?? value._id)
                    }
                    disabled={loadingGroups || form.is_admin}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Grupos"
                        required={!form.is_admin}
                        placeholder={
                          form.groups.length === 0
                            ? "Selecciona uno o varios grupos"
                            : ""
                        }
                        helperText={
                          form.is_admin
                            ? "Los administradores tienen acceso a todos los grupos."
                            : loadingGroups
                            ? "Cargando grupos…"
                            : "Puedes seleccionar uno o varios grupos."
                        }
                      />
                    )}
                  />
                </Grid>

                {/* Switches */}
                <Grid
                  item
                  xs={12}
                  display="flex"
                  alignItems="center"
                  gap={3}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.is_admin}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            is_admin: e.target.checked,
                            groups: e.target.checked ? [] : p.groups,
                          }))
                        }
                      />
                    }
                    label="Administrador"
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!form.disabled}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            disabled: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Suspendido"
                  />
                </Grid>

                {/* Botones */}
                <Grid item xs={12} display="flex" gap={1} mt={1}>
                  <SoftButton
                    onClick={handleSave}
                    disabled={saving || loadingGroups}
                    color="dark"
                    variant="gradient"
                  >
                    {saving ? "Guardando…" : "Guardar"}
                  </SoftButton>

                  <SoftButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(-1)}
                  >
                    Cancelar
                  </SoftButton>
                </Grid>

              </Grid>
            </Card>
          </Grid>

          {/* Derecha */}
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3 }}>
              <SoftTypography variant="h6" mb={2}>
                Permisos
              </SoftTypography>

              {form.is_admin ? (
                <Alert severity="info">
                  Los administradores tienen acceso total y no requieren permisos específicos.
                </Alert>
              ) : (
                <PermissionUser
                  value={permKeys}
                  onChange={(next) => {
                    setPermKeys(Array.isArray(next) ? next : []);
                  }}
                />
              )}
            </Card>
          </Grid>

        </Grid>
      </SoftBox>

      <Footer />
    </DashboardLayout>
  );
}